import { FpsCollector } from './collectors/fps';
import { FpsSampleBuffer } from './collectors/fps-sample-buffer';
import { VideoFpsCollector } from './collectors/video-fps';
import { Tracer } from './tracer';
import { FpsSample, PerfWindowSnapshot, SegmentFpsData, VideoFpsSource, WindowKind } from './types';

/**
 * 단일 측정 window(mirror 또는 match 구간).
 * 언제든 활성 window는 0개 또는 1개. 종료 시 snapshot을 리포트한다.
 */
export class PerfWindow {
  public readonly kind: WindowKind;

  public readonly id: string;

  public readonly matchId: string | null;

  public readonly startedAt = performance.now();

  public readonly startedAtEpoch = Date.now();

  public endedAt: number | null = null;

  public readonly tracer = new Tracer();

  private readonly renderCollectors = {
    local: new VideoFpsCollector(),
    'local-send': new VideoFpsCollector(),
    peer: new VideoFpsCollector(),
  };

  private readonly sourceFps = {
    local: new FpsSampleBuffer(),
    peer: new FpsSampleBuffer(),
  };

  private readonly encodeFps = new FpsSampleBuffer();

  private readonly pageFps = new FpsSampleBuffer();

  constructor(kind: WindowKind, id: string, matchId: string | null = null) {
    this.kind = kind;
    this.id = id;
    this.matchId = matchId;
  }

  public recordPageFps(sample: FpsSample): void {
    this.pageFps.push(sample.fps, sample.timestamp);
  }

  public recordVideoFrame(source: VideoFpsSource): void {
    this.renderCollectors[source].recordFrame();
  }

  public reportSourceFps(source: 'local' | 'peer', fps: number): void {
    this.sourceFps[source].push(fps);
  }

  public reportEncodeFps(fps: number): void {
    this.encodeFps.push(fps);
  }

  public finalize(): void {
    if (this.endedAt == null) this.endedAt = performance.now();
  }

  public getSegmentSnapshot(fromTimestamp: number, toTimestamp: number): SegmentFpsData {
    return {
      pageFps: this.pageFps.getSegmentSnapshot(fromTimestamp, toTimestamp),
      localPipeFps: this.renderCollectors.local.getSegmentSnapshot(fromTimestamp, toTimestamp),
      localSendPipeFps: this.renderCollectors['local-send'].getSegmentSnapshot(
        fromTimestamp,
        toTimestamp
      ),
      localSourceFps: this.sourceFps.local.getSegmentSnapshot(fromTimestamp, toTimestamp),
      localEncodeFps: this.encodeFps.getSegmentSnapshot(fromTimestamp, toTimestamp),
      peerInFps: this.sourceFps.peer.getSegmentSnapshot(fromTimestamp, toTimestamp),
      peerPipeFps: this.renderCollectors.peer.getSegmentSnapshot(fromTimestamp, toTimestamp),
    };
  }

  /**
   * @param options.withSamples raw sample 배열을 snapshot에 포함할지 여부.
   *   reporter는 percentile만 사용하므로 false가 기본. Debug UI sparkline에만 필요.
   */
  public getSnapshot(options: { withSamples?: boolean } = {}): PerfWindowSnapshot {
    const withSamples = options.withSamples ?? false;
    const end = this.endedAt ?? performance.now();
    const durationMs = Math.round(end - this.startedAt);
    return {
      kind: this.kind,
      id: this.id,
      matchId: this.matchId,
      startedAt: this.startedAt,
      startedAtEpoch: this.startedAtEpoch,
      endedAt: this.endedAt,
      durationMs,
      fps: this.pageFps.getSnapshot(withSamples),
      videoFps: {
        local: {
          render: this.renderCollectors.local.getSnapshot(withSamples),
          sendRender: this.renderCollectors['local-send'].getSnapshot(withSamples),
          source: this.sourceFps.local.getSnapshot(withSamples),
          encode: this.encodeFps.getSnapshot(withSamples),
        },
        peer: {
          render: this.renderCollectors.peer.getSnapshot(withSamples),
          sendRender: null,
          source: this.sourceFps.peer.getSnapshot(withSamples),
          encode: null,
        },
      },
      tracer: this.tracer.getSummary(durationMs),
    };
  }
}

/**
 * 전역 FPS collector + 단일 current window 관리자.
 *
 * 어느 시점이든 current window는 최대 1개 (또는 null).
 * transitionTo() 호출이 곧 이전 window 종료 보고 트리거.
 */
export class PerfAggregator {
  public readonly fps = new FpsCollector();

  private current: PerfWindow | null = null;

  private started = false;

  private fpsUnsubscribe: (() => void) | null = null;

  /** SSR/미지원 환경에서는 graceful no-op */
  public start(): void {
    if (this.started || typeof window === 'undefined') return;
    this.started = true;
    this.fps.start();
    this.fpsUnsubscribe = this.fps.onSample((sample) => {
      this.current?.recordPageFps(sample);
    });
  }

  public stop(): void {
    if (!this.started) return;
    this.fps.stop();
    this.fpsUnsubscribe?.();
    this.fpsUnsubscribe = null;
    this.current = null;
    this.started = false;
  }

  /**
   * 현재 window를 종료하고 새 window로 전환.
   * 종료된 이전 window의 snapshot을 반환 (초기 호출이면 null).
   */
  public transitionTo(
    kind: WindowKind,
    id: string,
    matchId: string | null = null
  ): PerfWindowSnapshot | null {
    let endedSnapshot: PerfWindowSnapshot | null = null;
    if (this.current) {
      this.current.finalize();
      endedSnapshot = this.current.getSnapshot();
    }
    this.current = new PerfWindow(kind, id, matchId);
    return endedSnapshot;
  }

  public getCurrent(): PerfWindow | null {
    return this.current;
  }

  public getCurrentSnapshot(options: { withSamples?: boolean } = {}): PerfWindowSnapshot | null {
    return this.current?.getSnapshot(options) ?? null;
  }

  public recordToTracer(label: string, durationMs: number): void {
    this.current?.tracer.record(label, durationMs);
  }

  public recordVideoFrame(source: VideoFpsSource): void {
    this.current?.recordVideoFrame(source);
  }

  public reportSourceFps(source: 'local' | 'peer', fps: number): void {
    this.current?.reportSourceFps(source, fps);
  }

  public reportEncodeFps(fps: number): void {
    this.current?.reportEncodeFps(fps);
  }
}
